import { logAPIError } from '../../utils/errorLogger'
import type {
  MarketplaceJob,
  MarketplaceWorkflowStatus,
  PrintProfile,
  MarketplaceSliceEstimate,
  ManufacturingPackageResponse,
  MarketplaceJobOffersResponse,
  MarketplaceBid,
  MarketplaceQuote,
  MarketplaceFarmSelectionResponse,
  MarketplaceFarm,
  MarketplaceJobCreateRequest,
  MarketplaceBidCreateRequest,
  MarketplaceQuoteCreateRequest,
  MarketplaceNotification,
  MarketplaceJobStatusUpdateResponse
} from '../types'

/**
 * Type signature for the makeRequest method from BackendService
 */
export type MakeRequest = <T>(endpoint: string, options?: RequestInit) => Promise<T>

/**
 * Type signature for the makeBinaryRequest method from BackendService
 */
export type MakeBinaryRequest = (endpoint: string, options?: RequestInit) => Promise<ArrayBuffer>

/**
 * List marketplace jobs for the current doctor
 */
export async function listMarketplaceJobs(makeRequest: MakeRequest): Promise<MarketplaceJob[]> {
  try {
    const response = await makeRequest<{ success: boolean; jobs: MarketplaceJob[] }>('/api/marketplace/jobs', {
      method: 'GET'
    })
    return response.jobs || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace jobs'
    logAPIError('/api/marketplace/jobs', 'GET', 0, errorMessage)
    throw error
  }
}

export async function listPrintProfiles(makeRequest: MakeRequest): Promise<PrintProfile[]> {
  try {
    const response = await makeRequest<{ success: boolean; profiles: PrintProfile[] }>('/api/print-profiles', {
      method: 'GET'
    })
    return response.profiles || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load print profiles'
    logAPIError('/api/print-profiles', 'GET', 0, errorMessage)
    throw error
  }
}

export async function createPrintProfile(
  makeRequest: MakeRequest,
  profile: Omit<PrintProfile, 'id'>
): Promise<PrintProfile> {
  try {
    const response = await makeRequest<{ success: boolean; profile: PrintProfile }>('/api/print-profiles', {
      method: 'POST',
      body: JSON.stringify({
        name: profile.name,
        description: profile.description,
        printer_model: profile.printerModel,
        material: profile.material,
        nozzle_diameter_mm: profile.nozzleDiameterMm,
        layer_height_mm: profile.layerHeightMm,
        infill_density: profile.infillDensity,
        print_speed_mm_s: profile.printSpeedMmS,
        filament_density_g_cm3: profile.filamentDensityGcm3,
        filament_diameter_mm: profile.filamentDiameterMm,
        wall_count: profile.wallCount,
        support_enabled: profile.supportEnabled,
        build_volume_x_mm: profile.buildVolume?.x,
        build_volume_y_mm: profile.buildVolume?.y,
        build_volume_z_mm: profile.buildVolume?.z,
        raw_settings: profile.rawSettings || {}
      })
    })
    return response.profile
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create print profile'
    logAPIError('/api/print-profiles', 'POST', 0, errorMessage, profile)
    throw error
  }
}

export async function importPrintProfile(
  baseURL: string,
  authToken: string | null,
  file: File
): Promise<PrintProfile> {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${baseURL}/api/print-profiles/import`, {
      method: 'POST',
      headers: {
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      },
      body: formData,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const payload = await response.json()
    return payload.profile
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to import print profile'
    logAPIError('/api/print-profiles/import', 'POST', 0, errorMessage)
    throw error
  }
}

export async function exportPrintProfile(
  makeRequest: MakeRequest,
  profileId: string
): Promise<Record<string, any>> {
  try {
    return await makeRequest<Record<string, any>>(`/api/print-profiles/${profileId}/export`, {
      method: 'GET'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to export print profile'
    logAPIError(`/api/print-profiles/${profileId}/export`, 'GET', 0, errorMessage)
    throw error
  }
}

export async function estimateMarketplaceJob(
  makeRequest: MakeRequest,
  input: {
    stlFileId: string
    profileId?: string
    profileOverrides?: Record<string, any>
  }
): Promise<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }> {
  try {
    return await makeRequest<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }>('/api/marketplace/estimate', {
      method: 'POST',
      body: JSON.stringify({
        stl_file_id: input.stlFileId,
        profile_id: input.profileId,
        profile_overrides: input.profileOverrides || undefined
      })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to estimate marketplace job'
    logAPIError('/api/marketplace/estimate', 'POST', 0, errorMessage, input)
    throw error
  }
}

export async function getMarketplaceJobManufacturingPackage(
  makeRequest: MakeRequest,
  jobId: string
): Promise<ManufacturingPackageResponse> {
  try {
    return await makeRequest<ManufacturingPackageResponse>(`/api/marketplace/jobs/${jobId}/manufacturing-package`, {
      method: 'GET'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load manufacturing package'
    logAPIError(`/api/marketplace/jobs/${jobId}/manufacturing-package`, 'GET', 0, errorMessage)
    throw error
  }
}

export async function estimateMarketplaceJobForFarm(
  makeRequest: MakeRequest,
  jobId: string,
  input: {
    profileId?: string
    profileOverrides?: Record<string, any>
  }
): Promise<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }> {
  try {
    return await makeRequest<{ profile: Record<string, any>; stlMetrics: Record<string, any>; estimate: MarketplaceSliceEstimate }>(`/api/marketplace/jobs/${jobId}/estimate`, {
      method: 'POST',
      body: JSON.stringify({
        profile_id: input.profileId,
        profile_overrides: input.profileOverrides || undefined
      })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to re-estimate marketplace job'
    logAPIError(`/api/marketplace/jobs/${jobId}/estimate`, 'POST', 0, errorMessage, input)
    throw error
  }
}

export async function downloadMarketplaceJobSTL(
  makeBinaryRequest: MakeBinaryRequest,
  jobId: string
): Promise<ArrayBuffer> {
  return await makeBinaryRequest(`/api/marketplace/jobs/${jobId}/stl/download`, {
    method: 'GET'
  })
}

/**
 * Create a marketplace job for the current doctor
 */
export async function createMarketplaceJob(
  makeRequest: MakeRequest,
  job: MarketplaceJobCreateRequest
): Promise<MarketplaceJob> {
  try {
    const response = await makeRequest<{ success: boolean; job: MarketplaceJob }>('/api/marketplace/jobs', {
      method: 'POST',
      body: JSON.stringify({
        stl_file_id: job.stlFileId,
        profile_id: job.profileId,
        profile_overrides: job.profileOverrides,
        title: job.title,
        description: job.description,
        material: job.material,
        quality_level: job.qualityLevel,
        deadline: job.deadline,
        pricing_model: job.pricingModel,
        fixed_price: job.pricingModel === 'fixed_price' ? job.fixedPrice : undefined
      })
    })
    return response.job
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to create marketplace job'
    logAPIError('/api/marketplace/jobs', 'POST', 0, errorMessage, job)
    throw error
  }
}

/**
 * Load doctor-owned job offers
 */
export async function getMarketplaceJobOffers(
  makeRequest: MakeRequest,
  jobId: string
): Promise<MarketplaceJobOffersResponse> {
  try {
    const response = await makeRequest<{ success: boolean; bids: MarketplaceBid[]; quotes?: MarketplaceQuote[] }>(`/api/marketplace/jobs/${jobId}/bids`, {
      method: 'GET'
    })
    return {
      bids: response.bids || [],
      quotes: response.quotes || []
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace offers'
    logAPIError(`/api/marketplace/jobs/${jobId}/bids`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Load doctor-owned job bids
 */
export async function getMarketplaceJobBids(
  makeRequest: MakeRequest,
  jobId: string
): Promise<MarketplaceBid[]> {
  const response = await getMarketplaceJobOffers(makeRequest, jobId)
  return response.bids
}

/**
 * Accept a marketplace bid
 */
export async function acceptMarketplaceBid(
  makeRequest: MakeRequest,
  bidId: string
): Promise<{ success: boolean; bidId: string; jobId: string; message: string }> {
  try {
    return await makeRequest<{ success: boolean; bidId: string; jobId: string; message: string }>(`/api/marketplace/bids/${bidId}/accept`, {
      method: 'PUT'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to accept marketplace bid'
    logAPIError(`/api/marketplace/bids/${bidId}/accept`, 'PUT', 0, errorMessage)
    throw error
  }
}

/**
 * Select a bid or fixed-price quote for a marketplace job
 */
export async function selectMarketplaceFarm(
  makeRequest: MakeRequest,
  jobId: string,
  selection: { bidId?: string; quoteId?: string; paymentMethod?: 'card' }
): Promise<MarketplaceFarmSelectionResponse> {
  try {
    return await makeRequest<MarketplaceFarmSelectionResponse>(`/api/jobs/${jobId}/select-farm`, {
      method: 'POST',
      body: JSON.stringify({
        bid_id: selection.bidId,
        quote_id: selection.quoteId,
        payment_method: selection.paymentMethod
      })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to select marketplace farm'
    logAPIError(`/api/jobs/${jobId}/select-farm`, 'POST', 0, errorMessage, selection)
    throw error
  }
}

/**
 * List farms available in the marketplace
 */
export async function listMarketplaceFarms(makeRequest: MakeRequest): Promise<MarketplaceFarm[]> {
  try {
    const response = await makeRequest<{ success: boolean; farms: MarketplaceFarm[] }>('/api/marketplace/farms', {
      method: 'GET'
    })
    return response.farms || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace farms'
    logAPIError('/api/marketplace/farms', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * List jobs available to farms
 */
export async function listAvailableMarketplaceJobs(makeRequest: MakeRequest): Promise<MarketplaceJob[]> {
  try {
    const response = await makeRequest<{ success: boolean; jobs: MarketplaceJob[] }>('/api/marketplace/jobs/available', {
      method: 'GET'
    })
    return response.jobs || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load available marketplace jobs'
    logAPIError('/api/marketplace/jobs/available', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * List active marketplace jobs assigned to the current farm
 */
export async function listAssignedMarketplaceJobs(makeRequest: MakeRequest): Promise<MarketplaceJob[]> {
  try {
    const response = await makeRequest<{ success: boolean; jobs: MarketplaceJob[] }>('/api/farms/jobs/active', {
      method: 'GET'
    })
    return response.jobs || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load active marketplace jobs'
    logAPIError('/api/farms/jobs/active', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Submit a marketplace bid
 */
export async function submitMarketplaceBid(
  makeRequest: MakeRequest,
  jobId: string,
  bid: MarketplaceBidCreateRequest
): Promise<{ success: boolean; bidId: string; message: string }> {
  try {
    return await makeRequest<{ success: boolean; bidId: string; message: string }>(`/api/marketplace/jobs/${jobId}/bids`, {
      method: 'POST',
      body: JSON.stringify({
        bid_amount: bid.bidAmount,
        estimated_days: bid.estimatedDays,
        notes: bid.notes || '',
        profile_id: bid.profileId,
        profile_overrides: bid.profileOverrides,
        estimated_material_grams: bid.estimatedMaterialGrams,
        estimated_print_time_minutes: bid.estimatedPrintTimeMinutes,
        estimated_material_cost: bid.estimatedMaterialCost,
        estimated_total_cost: bid.estimatedTotalCost,
        validity_hours: bid.validityHours ?? 48
      })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to submit marketplace bid'
    logAPIError(`/api/marketplace/jobs/${jobId}/bids`, 'POST', 0, errorMessage, bid)
    throw error
  }
}

/**
 * Submit a marketplace quote for a fixed-price job
 */
export async function submitMarketplaceQuote(
  makeRequest: MakeRequest,
  jobId: string,
  quote: MarketplaceQuoteCreateRequest
): Promise<{ success: boolean; quoteId: string; message: string }> {
  try {
    return await makeRequest<{ success: boolean; quoteId: string; message: string }>(`/api/marketplace/jobs/${jobId}/quotes`, {
      method: 'POST',
      body: JSON.stringify({
        quote_amount: quote.quoteAmount,
        estimated_days: quote.estimatedDays,
        notes: quote.notes || ''
      })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to submit marketplace quote'
    logAPIError(`/api/marketplace/jobs/${jobId}/quotes`, 'POST', 0, errorMessage, quote)
    throw error
  }
}

/**
 * Confirm delivery on a shipped marketplace job (doctor side)
 */
export async function confirmMarketplaceJobDelivered(
  makeRequest: MakeRequest,
  jobId: string
): Promise<{ success: boolean; jobId: string; status: MarketplaceWorkflowStatus; job: MarketplaceJob; message: string }> {
  try {
    return await makeRequest<{
      success: boolean
      jobId: string
      status: MarketplaceWorkflowStatus
      job: MarketplaceJob
      message: string
    }>(`/api/marketplace/jobs/${jobId}/confirm-delivered`, {
      method: 'PUT'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to confirm marketplace delivery'
    logAPIError(`/api/marketplace/jobs/${jobId}/confirm-delivered`, 'PUT', 0, errorMessage)
    throw error
  }
}

/**
 * List the current farm's submitted marketplace bids
 */
export async function listMyMarketplaceBids(makeRequest: MakeRequest): Promise<MarketplaceBid[]> {
  try {
    const response = await makeRequest<{ success: boolean; bids: MarketplaceBid[] }>('/api/marketplace/bids/my-bids', {
      method: 'GET'
    })
    return response.bids || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace bids'
    logAPIError('/api/marketplace/bids/my-bids', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * List the current farm's submitted marketplace quotes
 */
export async function listMyMarketplaceQuotes(makeRequest: MakeRequest): Promise<MarketplaceQuote[]> {
  try {
    const response = await makeRequest<{ success: boolean; quotes: MarketplaceQuote[] }>('/api/marketplace/quotes/my-quotes', {
      method: 'GET'
    })
    return response.quotes || []
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace quotes'
    logAPIError('/api/marketplace/quotes/my-quotes', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Withdraw a pending marketplace bid owned by the current farm
 */
export async function withdrawMarketplaceBid(
  makeRequest: MakeRequest,
  bidId: string
): Promise<{ success: boolean; bid: MarketplaceBid; message: string }> {
  try {
    return await makeRequest<{ success: boolean; bid: MarketplaceBid; message: string }>(`/api/marketplace/bids/${bidId}`, {
      method: 'DELETE'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel marketplace bid'
    logAPIError(`/api/marketplace/bids/${bidId}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

/**
 * Withdraw a pending marketplace quote owned by the current farm
 */
export async function withdrawMarketplaceQuote(
  makeRequest: MakeRequest,
  quoteId: string
): Promise<{ success: boolean; quote: MarketplaceQuote; message: string }> {
  try {
    return await makeRequest<{ success: boolean; quote: MarketplaceQuote; message: string }>(`/api/marketplace/quotes/${quoteId}`, {
      method: 'DELETE'
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to cancel marketplace quote'
    logAPIError(`/api/marketplace/quotes/${quoteId}`, 'DELETE', 0, errorMessage)
    throw error
  }
}

/**
 * Advance the workflow status of an active marketplace job assigned to the current farm
 */
export async function updateMarketplaceJobStatus(
  makeRequest: MakeRequest,
  jobId: string,
  status: MarketplaceWorkflowStatus
): Promise<MarketplaceJobStatusUpdateResponse> {
  try {
    return await makeRequest<MarketplaceJobStatusUpdateResponse>(`/api/farms/jobs/${jobId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    })
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update marketplace job status'
    logAPIError(`/api/farms/jobs/${jobId}/status`, 'PUT', 0, errorMessage, { status })
    throw error
  }
}

/**
 * List marketplace inbox notifications for the current doctor or farm
 */
export async function listMarketplaceNotifications(
  makeRequest: MakeRequest
): Promise<{ notifications: MarketplaceNotification[]; unreadCount: number }> {
  try {
    const response = await makeRequest<{
      success: boolean
      notifications: MarketplaceNotification[]
      unreadCount: number
    }>('/api/marketplace/notifications', {
      method: 'GET'
    })

    return {
      notifications: response.notifications || [],
      unreadCount: response.unreadCount || 0
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load marketplace notifications'
    logAPIError('/api/marketplace/notifications', 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Mark a marketplace inbox notification as read
 */
export async function markMarketplaceNotificationRead(
  makeRequest: MakeRequest,
  notificationId: string
): Promise<MarketplaceNotification> {
  try {
    const response = await makeRequest<{
      success: boolean
      notification: MarketplaceNotification
      message: string
    }>(`/api/marketplace/notifications/${notificationId}/read`, {
      method: 'PUT'
    })

    return response.notification
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to mark notification as read'
    logAPIError(`/api/marketplace/notifications/${notificationId}/read`, 'PUT', 0, errorMessage)
    throw error
  }
}
